import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  Col,
  Button,
  Row,
  Typography,
  Layout,
  Checkbox,
  TimePicker,
  Tabs,
  InputNumber,
  List,
  Tag,
  Empty,
  Skeleton,
  Space,
  Divider,
} from "antd";
import { useNavigate } from "react-router";
import { CloseCircleOutlined } from "@ant-design/icons";
import { Post } from "../../config/api/post";
import { Get } from "../../config/api/get";
import { MEETING, SCHEDULE, SESSION } from "../../config/constants/api";
import { ImageUrl } from "../../helpers/Functions";
import swal from "sweetalert";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import dayjs from "dayjs";
import "./meetings.css";

const getDefaultDays = () => [
  { day: "Sunday", selected: false, dayNo: 0, slots: [] },
  { day: "Monday", selected: false, dayNo: 1, slots: [] },
  { day: "Tuesday", selected: false, dayNo: 2, slots: [] },
  { day: "Wednesday", selected: false, dayNo: 3, slots: [] },
  { day: "Thursday", selected: false, dayNo: 4, slots: [] },
  { day: "Friday", selected: false, dayNo: 5, slots: [] },
  { day: "Saturday", selected: false, dayNo: 6, slots: [] },
];

function Meetings() {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.userData);
  const token = useSelector((state) => state.user.userToken);
  const panelRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(getDefaultDays());
  const [selectedDate, setSelectedDate] = useState(0);
  const [slot, setSlot] = useState({ startTime: null, endTime: null });
  const [meetingCharge, setMeetingCharge] = useState(null);
  const [meetingChargeLoading, setMeetingChargeLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("availability");
  const [coachSessions, setCoachSessions] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [actionSessionId, setActionSessionId] = useState(null);

  useEffect(() => {
    getSchedule();
    getMeetingCharge();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab === "bookings") {
      loadCoachSessions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const applyScheduleData = (data = []) => {
    const nextDays = getDefaultDays();
    data.forEach((responseObj) => {
      const day = nextDays.find((item) => item.dayNo === responseObj.day);
      if (day) {
        day.selected = true;
        day.slots = [...(responseObj.timeSlots || [])];
      }
    });
    setDays(nextDays);
  };

  const getSchedule = () => {
    try {
      Get(SCHEDULE.getMySchedule, token).then((response) => {
        if (response?.status) {
          applyScheduleData(response?.data || []);
        }
      });
    } catch (error) {
      console.log("error", error);
    }
  };

  const getMeetingCharge = () => {
    setMeetingChargeLoading(true);
    Get(MEETING.getMyMeetingCharge, token)
      .then((response) => {
        if (response?.status) {
          setMeetingCharge(response?.data?.meetingCharge?.charge ?? 0);
        } else {
          setMeetingCharge(0);
        }
      })
      .catch(() => {
        swal("Oops!", "Unable to load meeting charge", "error");
      })
      .finally(() => {
        setMeetingChargeLoading(false);
      });
  };

  const saveSchedule = () => {
    let hasError = false;
    const availability = days
      .filter((item) => item.selected)
      .map((item) => {
        if (item.slots.length === 0) {
          swal("Error!", "Please enter timeslots for all selected days", "error");
          hasError = true;
          return null;
        }
        return { day: item.dayNo, timeSlots: item.slots };
      })
      .filter(Boolean);

    if (hasError) {
      return;
    }

    setLoading(true);
    const data = {
      coachId: user._id,
      availability,
    };

    Post(SCHEDULE.addSchedule, data, token, null)
      .then((response) => {
        if (response?.data?.status) {
          swal("Success!", "Schedule updated successfully", "success");
          setSlot({ startTime: null, endTime: null });
          getSchedule();
        } else {
          swal("Oops!", response?.data?.message, "error");
        }
      })
      .catch(() => {
        swal("Oops!", "Unable to save schedule", "error");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleChecked = (checked, indx) => {
    setDays((prev) =>
      prev.map((day, index) =>
        index === indx ? { ...day, selected: checked } : day
      )
    );
  };

  const addSlot = () => {
    if (!slot.startTime || !slot.endTime) {
      swal("Error!", "Select start and end time", "error");
      return;
    }
    if (slot.startTime > slot.endTime) {
      swal("Error!", "End time must be greater than start time", "error");
      return;
    }
    setDays((prev) =>
      prev.map((day) => {
        if (day.dayNo !== selectedDate) return day;
        return { ...day, slots: [...day.slots, slot] };
      })
    );
    setSlot({ startTime: null, endTime: null });
  };

  const removeSlot = (indx) => {
    setDays((prev) =>
      prev.map((day) => {
        if (day.dayNo !== selectedDate) return day;
        return {
          ...day,
          slots: day.slots.filter((_, index) => index !== indx),
        };
      })
    );
  };

  const loadCoachSessions = () => {
    setBookingsLoading(true);
    Get(SESSION.coach, token)
      .then((response) => {
        if (response?.status) {
          setCoachSessions(response?.data?.sessions || []);
        } else {
          setCoachSessions([]);
        }
      })
      .catch(() => setCoachSessions([]))
      .finally(() => setBookingsLoading(false));
  };

  const formatSlotLabel = (slotItem) => {
    const start = dayjs(slotItem.startTime);
    const end = dayjs(slotItem.endTime);
    if (!start.isValid() || !end.isValid()) return "—";
    return `${start.format("hh:mm A")} - ${end.format("hh:mm A")}`;
  };

  const approveSession = (sessionId) => {
    setActionSessionId(sessionId);
    Post(`${SESSION.approve}${sessionId}/approve`, {}, token, null)
      .then((response) => {
        const api = response?.data || {};
        if (api?.status) {
          swal("Success!", api?.message || "Session approved", "success");
          loadCoachSessions();
        } else {
          swal("Oops!", api?.message || "Could not approve", "error");
        }
      })
      .catch(() => swal("Oops!", "Could not approve session", "error"))
      .finally(() => setActionSessionId(null));
  };

  const declineSession = (sessionId) => {
    setActionSessionId(sessionId);
    Post(`${SESSION.decline}${sessionId}/decline`, {}, token, null)
      .then((response) => {
        const api = response?.data || {};
        if (api?.status) {
          swal("Success!", api?.message || "Session declined", "success");
          loadCoachSessions();
        } else {
          swal("Oops!", api?.message || "Could not decline", "error");
        }
      })
      .catch(() => swal("Oops!", "Could not decline session", "error"))
      .finally(() => setActionSessionId(null));
  };

  const isUpcomingSessionDay = (date) =>
    dayjs(date).startOf("day").valueOf() >= dayjs().startOf("day").valueOf();

  const pendingSessions = coachSessions.filter((s) => s.status === "PENDING");
  const approvedSessions = coachSessions.filter((s) => s.status === "APPROVED");
  const upcomingPaidSessions = coachSessions.filter(
    (s) => s.status === "PAID" && isUpcomingSessionDay(s.date)
  );

  const startMeeting = (sessionId) => {
    setActionSessionId(sessionId);
    Post(`${SESSION.startMeeting}${sessionId}`, {}, token, null)
      .then((response) => {
        const api = response?.data || {};
        if (api?.status && api?.data?.startUrl) {
          window.open(api.data.startUrl, "_blank", "noopener,noreferrer");
          swal(
            "Success!",
            api?.message || "Zoom host view opened in a new tab",
            "success"
          );
          loadCoachSessions();
        } else {
          swal("Oops!", api?.message || "Could not start meeting", "error");
        }
      })
      .catch(() => swal("Oops!", "Could not start meeting", "error"))
      .finally(() => setActionSessionId(null));
  };

  const scrollToPanel = () => {
    panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const openBookings = () => {
    setActiveTab("bookings");
    setTimeout(scrollToPanel, 50);
  };

  const openAvailability = () => {
    setActiveTab("availability");
    setTimeout(scrollToPanel, 50);
  };

  const renderSessionList = (items, showActions) => (
    <List
      className="mt-session-list"
      dataSource={items}
      locale={{ emptyText: <Empty description="No bookings in this section" /> }}
      renderItem={(item) => (
        <List.Item
          key={item._id}
          className="mt-session-item"
          actions={
            showActions
              ? [
                  <Button
                    key="approve"
                    type="primary"
                    size="small"
                    className="mt-btn-approve"
                    loading={actionSessionId === item._id}
                    onClick={() => approveSession(item._id)}
                  >
                    Approve
                  </Button>,
                  <Button
                    key="decline"
                    danger
                    size="small"
                    className="mt-btn-decline"
                    disabled={actionSessionId === item._id}
                    onClick={() => declineSession(item._id)}
                  >
                    Decline
                  </Button>,
                ]
              : [
                  <Tag key="status" color="blue">
                    Awaiting payment
                  </Tag>,
                ]
          }
        >
          <List.Item.Meta
            title={
              <Space wrap>
                <span className="fontFamily1 mt-session-name">
                  {item.student?.firstName} {item.student?.lastName}
                </span>
                <Tag>{item.status}</Tag>
              </Space>
            }
            description={
              <div className="fontFamily1 mt-session-meta">
                <div>{dayjs(item.date).format("MMM D, YYYY")}</div>
                <div className="mt-session-slots">
                  {(item.slots || []).map((s, i) => (
                    <Tag key={i}>{formatSlotLabel(s)}</Tag>
                  ))}
                </div>
                <div>Total: ${item.charges}</div>
              </div>
            }
          />
        </List.Item>
      )}
    />
  );

  const renderUpcomingPaidList = (items) => (
    <List
      className="mt-session-list"
      dataSource={items}
      locale={{ emptyText: <Empty description="No upcoming paid sessions" /> }}
      renderItem={(item) => (
        <List.Item
          key={item._id}
          className="mt-session-item"
          actions={[
            <Button
              key="start"
              type="primary"
              className="mt-btn-primary"
              size="small"
              loading={actionSessionId === item._id}
              onClick={() => startMeeting(item._id)}
            >
              {item.meetingStartedAt ? "Open Zoom again" : "Start meeting"}
            </Button>,
          ]}
        >
          <List.Item.Meta
            title={
              <Space wrap>
                <span className="fontFamily1 mt-session-name">
                  {item.student?.firstName} {item.student?.lastName}
                </span>
                <Tag color="green">PAID</Tag>
                {item.meetingStartedAt ? (
                  <Tag color="processing">Started</Tag>
                ) : null}
              </Space>
            }
            description={
              <div className="fontFamily1 mt-session-meta">
                <div>{dayjs(item.date).format("MMM D, YYYY")}</div>
                <div className="mt-session-slots">
                  {(item.slots || []).map((s, i) => (
                    <Tag key={i}>{formatSlotLabel(s)}</Tag>
                  ))}
                </div>
                <div className="mt-session-hint">
                  Opens Zoom in a new browser tab. Session stays here until the end of the day.
                </div>
              </div>
            }
          />
        </List.Item>
      )}
    />
  );

  const bookingsTab = (
    <div className="mt-card">
      <Typography.Title level={4} className="fontFamily1 mt-card-title">
        Bookings
      </Typography.Title>
      <Typography.Paragraph className="fontFamily1 mt-card-lead">
        Approve requests, then start paid meetings in Zoom when it is time. Paid sessions remain
        listed until the end of the session day so you can re-open Zoom if needed.
      </Typography.Paragraph>
      {bookingsLoading ? (
        <Skeleton active />
      ) : (
        <>
          <Typography.Title level={5} className="fontFamily1 mt-card-subtitle">
            Upcoming meetings (paid)
          </Typography.Title>
          {renderUpcomingPaidList(upcomingPaidSessions)}
          <Divider />
          <Typography.Title level={5} className="fontFamily1 mt-card-subtitle">
            Pending approval
          </Typography.Title>
          {renderSessionList(pendingSessions, true)}
          <Divider />
          <Typography.Title level={5} className="fontFamily1 mt-card-subtitle">
            Approved (awaiting payment)
          </Typography.Title>
          {renderSessionList(approvedSessions, false)}
        </>
      )}
    </div>
  );

  const saveMeetingCharge = () => {
    const charge = Number(meetingCharge);
    if (Number.isNaN(charge) || charge < 0) {
      swal("Error!", "Charge must be 0 or greater", "error");
      return;
    }

    setMeetingChargeLoading(true);
    Post(MEETING.setMeetingCharge, { charge }, token, null)
      .then((response) => {
        if (response?.data?.status) {
          swal("Success!", "Meeting charge saved successfully", "success");
          setMeetingCharge(response?.data?.data?.meetingCharge?.charge ?? charge);
        } else {
          swal("Oops!", response?.data?.message, "error");
        }
      })
      .catch(() => {
        swal("Oops!", "Unable to save meeting charge", "error");
      })
      .finally(() => {
        setMeetingChargeLoading(false);
      });
  };

  const availabilityTab = (
    <div className="mt-card">
      <Typography.Title className="fontFamily1 mt-card-title" level={4}>
        Availability
      </Typography.Title>

      <div className="mt-days">
        {days.map((item, index) => (
          <button
            key={item.dayNo}
            type="button"
            className={`mt-day${item.dayNo === selectedDate ? " is-active" : ""}`}
            onClick={() => setSelectedDate(item.dayNo)}
          >
            <Checkbox
              checked={item.selected}
              onChange={(e) => handleChecked(e.target.checked, index)}
              onClick={(e) => e.stopPropagation()}
            >
              <span className="fontFamily1 mt-day-label">{item.day}</span>
            </Checkbox>
          </button>
        ))}
      </div>

      <Typography.Title className="fontFamily1 mt-card-subtitle" level={5}>
        Slots
      </Typography.Title>
      {days[selectedDate]?.slots.length === 0 && (
        <Typography.Text className="fontFamily1 mt-empty-slots">
          No Slots Added
        </Typography.Text>
      )}

      <Row gutter={[12, 12]} className="mt-slots-row">
        {days[selectedDate].slots.length > 0 &&
          days[selectedDate].slots.map((subItem, subIndex) => (
            <Col xs={24} md={8} lg={6} key={`${selectedDate}-${subIndex}`}>
              <div className="mt-slot-chip">
                <CloseCircleOutlined
                  onClick={() => removeSlot(subIndex)}
                  className="mt-slot-remove"
                />
                <Typography.Text className="fontFamily1">
                  {dayjs(new Date(subItem.startTime)).format("hh:mm A") +
                    " to " +
                    dayjs(new Date(subItem.endTime)).format("hh:mm A")}
                </Typography.Text>
              </div>
            </Col>
          ))}
      </Row>

      <Typography.Title className="fontFamily1 mt-card-subtitle" level={5}>
        Add Slots
      </Typography.Title>

      <Row gutter={[12, 12]} align="middle">
        <Col xs={12} md={6} lg={4}>
          <TimePicker
            size="large"
            className="mt-time"
            style={{ width: "100%" }}
            placeholder="Start Time"
            value={slot.startTime}
            onChange={(e) => setSlot({ ...slot, startTime: e })}
            format={"hh:mm:A"}
          />
        </Col>
        <Col xs={12} md={6} lg={4}>
          <TimePicker
            size="large"
            className="mt-time"
            style={{ width: "100%" }}
            placeholder="End Time"
            value={slot.endTime}
            onChange={(e) => setSlot({ ...slot, endTime: e })}
            format={"hh:mm:A"}
          />
        </Col>
        <Col xs={12} md={5}>
          <Button className="mt-btn-navy" size="large" onClick={addSlot}>
            Add
          </Button>
        </Col>
      </Row>

      <div className="mt-save-row">
        <Button type="primary" className="mt-btn-primary" onClick={saveSchedule}>
          {loading ? "Loading..." : "Save Schedule"}
        </Button>
      </div>
    </div>
  );

  const chargesTab = (
    <div className="mt-card">
      <Typography.Title level={4} className="fontFamily1 mt-card-title">
        Meeting Charges
      </Typography.Title>
      <Typography.Paragraph className="fontFamily1 mt-card-lead">
        Set the per-slot booking charge students will pay for your meetings.
      </Typography.Paragraph>
      <Row gutter={[12, 12]} align="middle">
        <Col xs={24} md={10}>
          <InputNumber
            min={0}
            style={{ width: "100%" }}
            value={meetingCharge}
            onChange={(value) => setMeetingCharge(value)}
            addonBefore="$"
            placeholder="Enter charge"
            size="large"
            className="mt-charge-input"
          />
        </Col>
        <Col xs={24} md={8}>
          <Button
            type="primary"
            className="mt-btn-primary"
            loading={meetingChargeLoading}
            onClick={saveMeetingCharge}
          >
            Save Charge
          </Button>
        </Col>
      </Row>
    </div>
  );

  return (
    <Layout className="mt-page">
      <section
        className="mt-hero"
        style={{ backgroundImage: `url(${ImageUrl("meeting-banner-new.png")})` }}
      >
        <div className="mt-hero-fade" aria-hidden />
        <div className="mt-wrap mt-hero-inner">
          <button
            type="button"
            className="mt-back"
            aria-label="Go back"
            onClick={() => navigate(-1)}
          >
            <FaArrowLeft />
          </button>

          <div className="mt-hero-copy">
            <span className="mt-hero-badge">MEETINGS</span>
            <h1 className="fontFamily1 mt-hero-title">
              Schedule Your <span>Meeting</span> with Us
            </h1>
            <p className="fontFamily1 mt-hero-sub">
              Choose a convenient time to connect with our team. We&apos;re here to help
              and answer any questions you have.
            </p>
            <div className="mt-hero-actions">
              <Button
                type="primary"
                size="large"
                className="mt-btn-primary"
                onClick={openBookings}
              >
                Book a Meeting <FaArrowRight />
              </Button>
              <Button size="large" className="mt-btn-outline" onClick={openAvailability}>
                Learn More
              </Button>
            </div>
          </div>
        </div>
      </section>

      <div className="mt-wrap mt-main" ref={panelRef}>
        <div className="mt-panel">
          <Tabs
            className="mt-tabs"
            activeKey={activeTab}
            onChange={setActiveTab}
            destroyInactiveTabPane={false}
            items={[
              {
                key: "availability",
                label: "Availability",
                children: availabilityTab,
              },
              {
                key: "charges",
                label: "Meeting Charges",
                children: chargesTab,
              },
              {
                key: "bookings",
                label: "Bookings",
                children: bookingsTab,
              },
            ]}
          />
        </div>
      </div>
    </Layout>
  );
}

export default Meetings;
